import { differenceWith, isEmpty, isEqual, some, union } from "lodash";
import React, { useEffect, useState } from "react";
import { TFilterDropDownData } from "../../../../fixtures/data-fixtures/ComponentConfigFixtures";
import useClickOutSide from "../../../../utils/custom-hooks/useClickOutside";
import { isArrayEqual } from "../../../../utils/utils";
import ZFButton from "../../../ui-component/ZFButton/ZFButton";
import {
	ButtonState,
	ButtonType,
} from "../../../ui-component/ZFButton/ZFButton.d";
import ZFIcon from "../../../ui-component/ZFIcon/ZFIcon";
import {
	FILTER_SELECTION_TYPE,
	IDropDownListProps,
	IDropDownProps,
} from "./DropDown.d";
import Checkbox from "./dropdownCheckbox";

const DropDown = (props: IDropDownProps<TFilterDropDownData>) => {
	const {
		config,
		basedOnSelection,
		rest,
		currentSelection,
		onDropDownFilterChange,
	} = props;

	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState([...currentSelection]);
	const [searchString, setSearchString] = useState("");

	const [filterSelectionType, setSelectedFilterType] = useState(FILTER_SELECTION_TYPE.NONE);

	const nodeRef = useClickOutSide(() => onClose());

	useEffect(() => {
		setSelected(currentSelection);
	}, [currentSelection]);

	const toggle = () => setOpen(!open);

	const onFilterChange = (item: TFilterDropDownData): void => {
		if (config.singleSelection) {
			setSelected([item]);
			return;
		}

		const updateSelectedFilter = some(selected, item)
			? differenceWith(selected, [item], isEqual)
			: union(selected, [item]);

		setSelected(updateSelectedFilter);
	};

	const onFilterChangeFromBasedOnSelection = (
		item: TFilterDropDownData
	): void => {
		switch (filterSelectionType) {
			case FILTER_SELECTION_TYPE.REST:
				setSelectedFilterType(FILTER_SELECTION_TYPE.BOTH);
				break;
			default:
				setSelectedFilterType(FILTER_SELECTION_TYPE.BASED_ON_SELECTION);
				break;
		}
		onFilterChange(item);
	};

	const onFilterChangeFromRest = (item: TFilterDropDownData): void => {
		switch (filterSelectionType) {
			case FILTER_SELECTION_TYPE.BASED_ON_SELECTION:
				setSelectedFilterType(FILTER_SELECTION_TYPE.BOTH);
				break;
			default:
				setSelectedFilterType(FILTER_SELECTION_TYPE.REST);
				break;
		}
		onFilterChange(item);
	};

	const onClose = () => {
		if (open) {
			setOpen(false);
			if (!isArrayEqual(selected, currentSelection)) {
				onDropDownFilterChange(
					selected,
					config.key,
					filterSelectionType
				);
			}
		}
	};

	return (
		<div className="zf-padding-r-s">
			<div ref={nodeRef}>
				<div className="align-item">
					<ZFButton
						onBtnClick={toggle}
						buttonType={ButtonType.secondary}
						state={
							[...basedOnSelection, ...rest].length > 0
								? ButtonState.default
								: ButtonState.disabled
						}
						buttonClasses={[
							"zf-text-medium zf-padding-l-s zf-padding-r-s zf-padding-t-xs zf-padding-b-xs",
						]}
					>
						<span className="zf-text-s zf-padding-r-xs zf-text-case-capitalize">
							{config.display_name}
						</span>
						<ZFIcon
							iconName={"dropdown"}
							iconClasses={[
								"zf-icon-react--margin-none",
								"zf-icon-react--size-xs",
							]}
						/>
					</ZFButton>
				</div>
				{open && (
					<div className="dd-list">
						<div className="dd-search-container">
							{config.allowSearchFilter && (
								<div className="dd-search zf-padding-l-s zf-padding-r-s zf-padding-t-xxs zf-padding-b-xxs">
									<input
										className="search-input"
										placeholder={
											config.searchPlaceholderText
										}
										onChange={(event) =>
											setSearchString(event.target.value)
										}
										value={searchString}
									/>
									<ZFIcon
										iconName="search"
										iconClasses={["zf-icon-react--size-s"]}
									/>
								</div>
							)}
						</div>
						<div className="dd-list-container">
							<DropDownList
								filters={basedOnSelection}
								selected={selected}
								header="Based On Selection"
								searchString={searchString}
								onFilterChange={
									onFilterChangeFromBasedOnSelection
								}
							/>
							<DropDownList
								filters={rest}
								selected={selected}
								header="All"
								searchString={searchString}
								onFilterChange={onFilterChangeFromRest}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default DropDown;

export const DropDownList = (props: IDropDownListProps) => {
	const { filters, searchString, selected, header, onFilterChange } = props;
	const search = (v1: string, v2: string) => {
		return (
			!v2 || isEmpty(v2) || v1.toLowerCase().includes(v2.toLowerCase())
		);
	};
	return (
		<>
			{filters.length > 0 && (
				<div>
					<div className="zf-text-font-size-xs zf-text-text-transform-case-upper zf-text-color-black-60 zf-margin-b-xxl-8 zf-margin-t-xxl-s">
						{header}
					</div>
					{filters.map((item) => {
						return (
							<div key={`bos_${item.value}`}>
								{search(item.value, searchString) && (
									<Checkbox
										item={item}
										isChecked={some(selected, item)}
										onFilterChange={onFilterChange}
									/>
								)}
							</div>
						);
					})}
				</div>
			)}
		</>
	);
};
